import { useEffect, useMemo, useState } from 'react'
import { X, Loader2, Trash2, Clock, CalendarClock, Calculator, History, StickyNote, AlertTriangle, CalendarOff, Fingerprint } from 'lucide-react'
import { postJson, patchJson, apiDelete } from '../../lib/api.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import {
  clockinTs, clockoutTs, hasClockin, shiftStart, shiftEnd, lateMinutes, otValue,
  travelFee, fmtTime, fmtDate, fmtDateTime, fmtMoney,
  tsToTimeInput, timeOnShiftDate, isOvernightShift, punchDateDrifts,
  relabelBranch, isLeaveRow,
} from '../../lib/clockin.js'

// Mutually exclusive by construction — one control, three states.
const OT_MODES = [
  { key: 'none',    label: 'ไม่มี OT',   onCls: 'bg-slate-600 text-white shadow-sm' },
  { key: 'allow',   label: 'อนุมัติ OT', onCls: 'bg-emerald-600 text-white shadow-sm' },
  { key: 'special', label: 'OT พิเศษ',   onCls: 'bg-indigo-600 text-white shadow-sm' },
]

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

function SectionHead({ icon: Icon, title, note }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-2.5">
      <Icon size={15} className="text-brand-600" strokeWidth={1.9} />
      <h3 className="text-[13.5px] font-semibold text-slate-700">{title}</h3>
      {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
    </div>
  )
}

// Pill toggle that always states BOTH sides — "ยังไม่อนุมัติ OT" reads as a
// fact, where an unchecked box reads as an unanswered question.
function Choice({ on, disabled, onClick, labelOn, labelOff, tone, dot, inline = false }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      className={`${inline ? 'inline-flex' : 'flex w-full'} items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium ring-1 transition disabled:opacity-50 ${tone}`}>
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      {on ? labelOn : labelOff}
    </button>
  )
}

// The list endpoint resolves created_by/modified_by through an addon that
// outputs only `code`; a bare id is the honest fallback when it cannot.
function actorLabel(actor, id) {
  const code = actor?.code
  if (code) return code
  const n = Number(id)
  return Number.isFinite(n) && n > 0 ? `#${n}` : '—'
}

function Meta({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span className="text-right min-w-0">
        <span className="text-slate-700 font-medium break-words">{value}</span>
        {sub && sub !== '—' && <span className="text-slate-400 ml-2 tabular-nums">{sub}</span>}
      </span>
    </div>
  )
}

function Stat({ label, value, tone = 'text-slate-800' }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2">
      <div className="text-[11.5px] text-slate-400">{label}</div>
      <div className={`text-[14px] font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}

/**
 * Drawer for one shift's clock-in. `row` is an employee_timetable record as the
 * report loaded it — the shift itself (date, type, branch, scheduled window) is
 * read-only here; only the punch, the OT approval and the note are writable.
 *
 * Xano keeps the stored timestamp for any field left blank, so a saved time can
 * be overwritten but not blanked — clearing is the ล้างการลงเวลา action, which
 * wipes the whole clockin{} object and logs it.
 */
export default function ClockinEditor({ row, employeeName, branchName, selfEdit, onClose, onSaved }) {
  // Time only — the day is the shift's, not the operator's (see timeOnShiftDate).
  const [clockin, setClockin] = useState(() => tsToTimeInput(clockinTs(row)))
  const [clockout, setClockout] = useState(() => tsToTimeInput(clockoutTs(row)))
  // OT is a THREE-way choice, not two booleans: none / อนุมัติ OT / OT พิเศษ.
  // They are mutually exclusive by Xano's own rule — clockin_calculation zeroes
  // clockout_price when special_OT is set, so "both" would silently mean
  // "special wins", and two independent checkboxes would imply otherwise.
  const [otMode, setOtMode] = useState(() => (
    row?.special_OT === true ? 'special' : row?.allow_OT === true ? 'allow' : 'none'
  ))
  // Leave-day attributes — a ลากิจ/ลาป่วย row has no punch, so these stand in
  // for the clock times. `unnotice` is Xano's field for ไม่แจ้งล่วงหน้า.
  const [unnotice, setUnnotice] = useState(() => row?.unnotice === true)
  const [medCert, setMedCert] = useState(() => row?.med_certificate === true)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirmClear, setConfirmClear] = useState(false)

  const punched = hasClockin(row)
  const locked = selfEdit
  const leave = isLeaveRow(row)
  const sickLeave = String(row?.type || '') === 'ลาป่วย'
  const unpaidLeave = String(row?.type || '') === 'Leave without pay'
  const logs = useMemo(() => [...(Array.isArray(row?.clockin?.log) ? row.clockin.log : [])].reverse(), [row])
  const notes = useMemo(() => [...(Array.isArray(row?.clockin?.remark) ? row.clockin.remark : [])].reverse(), [row])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmClear) onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, confirmClear])

  async function save() {
    if (leave) {
      setSaving(true); setErr('')
      try {
        await patchJson(`/api/hr/clockin/${row.id}`, {
          unnotice,
          // Only meaningful on a sick day; never sent for ลากิจ, so a type change
          // can't leave a stale certificate flag behind.
          ...(sickLeave ? { med_certificate: medCert } : {}),
        })
        onSaved?.()
      } catch (e) { setErr(String(e.message || e)) }
      finally { setSaving(false) }
      return
    }
    const inTs = timeOnShiftDate(row, clockin)
    let outTs = timeOnShiftDate(row, clockout)
    // Only a schedule that genuinely crosses midnight may push the clock-out to
    // the next day; anywhere else an out-before-in is a typo and stays an error.
    if (inTs != null && outTs != null && outTs <= inTs && isOvernightShift(row)) outTs += 86400000
    if (!punched && inTs == null) { setErr('กรุณาระบุเวลาเข้างาน'); return }
    if (inTs != null && outTs != null && outTs <= inTs) { setErr('เวลาออกงานต้องอยู่หลังเวลาเข้างาน'); return }
    setSaving(true); setErr('')
    try {
      const wantSpecial = otMode === 'special'
      if (wantSpecial !== (row?.special_OT === true)) {
        await patchJson(`/api/hr/clockin/${row.id}`, { special_OT: wantSpecial })
      }
      await postJson(`/api/hr/clockin/${row.id}`, {
        type: punched ? 'edit' : 'add',
        clockin: inTs ?? undefined,
        clockout: outTs ?? undefined,
        allow_OT: otMode === 'allow',
        remark: note.trim() || undefined,
      })
      onSaved?.()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setSaving(false) }
  }

  async function clearPunch() {
    setConfirmClear(false)
    setSaving(true); setErr('')
    try {
      await apiDelete(`/api/hr/clockin/${row.id}`)
      onSaved?.()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setSaving(false) }
  }

  const late = lateMinutes(row)
  const ot = otValue(row)

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-slate-900 truncate">{employeeName || row?.employee?.code || '—'}</h2>
            <p className="text-[12.5px] text-slate-500">
              {fmtDate(row?.date)} · {row?.type || '—'} · {relabelBranch(branchName) || '—'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {locked && (
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5 text-[12.5px] text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>นี่คือการลงเวลาของคุณเอง — ต้องให้ HR หรือผู้จัดการคนอื่นเป็นผู้แก้ไข</span>
            </div>
          )}
          {leave && (
            <div className="mt-3 rounded-xl bg-violet-50 ring-1 ring-violet-200 px-3 py-2.5 text-[12.5px] text-violet-800">
              วันนี้เป็นวัน{row.type} — ไม่มีการลงเวลาเข้า-ออกงาน · แก้ไขวัน/ประเภทการลาได้ที่แท็บ “การลา”
            </div>
          )}

          {/* scheduled window — read-only context for the numbers below */}
          <SectionHead icon={CalendarClock} title="กะที่กำหนด" note="แก้ไขได้ที่ตารางเวร" />
          <div className="grid grid-cols-3 gap-2">
            <Stat label="เข้ากะ" value={fmtTime(shiftStart(row))} />
            <Stat label="ออกกะ" value={fmtTime(shiftEnd(row))} />
            <Stat label="ประเภทกะ" value={row?.type || '—'} />
          </div>

          {leave ? (
            /* A leave day has no punch and can never earn OT, so the whole
               clock-in half is replaced rather than disabled — an OT toggle you
               may flip but that Xano will always evaluate to zero is a lie. */
            <>
              <SectionHead icon={CalendarOff} title="รายละเอียดการลา" />
              <div className="space-y-2">
                <Choice
                  on={unnotice} disabled={locked} onClick={() => setUnnotice((v) => !v)}
                  labelOn="ลาฉุกเฉิน (ไม่แจ้งล่วงหน้า)" labelOff="แจ้งลาล่วงหน้า"
                  tone={unnotice ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}
                  dot={unnotice ? 'bg-amber-500' : 'bg-slate-300'}
                />
                {sickLeave && (
                  <Choice
                    on={medCert} disabled={locked} onClick={() => setMedCert((v) => !v)}
                    labelOn="มีใบรับรองแพทย์" labelOff="ไม่มีใบรับรองแพทย์"
                    tone={medCert ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}
                    dot={medCert ? 'bg-emerald-500' : 'bg-slate-300'}
                  />
                )}
              </div>
              <p className="text-[11.5px] text-slate-400 mt-2">
                {sickLeave ? 'ใบรับรองแพทย์มีผลกับการนับวันลาป่วย'
                  : unpaidLeave ? 'Leave without pay ไม่นับในโควตาลากิจ · ลาฉุกเฉินคือการลาที่ไม่ได้แจ้งล่วงหน้า'
                    : 'การลากิจฉุกเฉินคือการลาที่ไม่ได้แจ้งล่วงหน้า'}
              </p>
            </>
          ) : (
            <>
              {/* the punch */}
              <SectionHead icon={Clock} title="เวลาเข้า-ออกงาน" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>เวลาเข้างาน</label>
                  <input type="time" value={clockin} disabled={locked}
                    onChange={(e) => setClockin(e.target.value)} className={FIELD} />
                </div>
                <div>
                  <label className={LABEL}>เวลาออกงาน</label>
                  <input type="time" value={clockout} disabled={locked}
                    onChange={(e) => setClockout(e.target.value)} className={FIELD} />
                </div>
              </div>
              <p className="text-[11.5px] text-slate-400 mt-1.5">
                วันที่ยึดตามตารางเวร — <span className="text-slate-500 font-medium">{fmtDate(row?.date)}</span> — แก้ได้เฉพาะเวลา ·
                เว้นว่างไว้ = คงเวลาเดิม (ลบเวลาที่บันทึกแล้วไม่ได้ ต้องใช้ “ล้างการลงเวลา”)
              </p>
              {punchDateDrifts(row) && (
                <div className="mt-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12px] text-amber-800">
                  เวลาที่บันทึกไว้เดิมอยู่คนละวันกับตารางเวร — เมื่อกดบันทึก ระบบจะย้ายมาเป็นวันที่ {fmtDate(row?.date)}
                </div>
              )}

              <div className="mt-3">
                <label className={LABEL}>การทำ OT</label>
                <div className="inline-flex rounded-xl ring-1 ring-slate-200 bg-white p-0.5">
                  {OT_MODES.map((m) => {
                    const on = otMode === m.key
                    return (
                      <button key={m.key} type="button" disabled={locked}
                        onClick={() => setOtMode(m.key)}
                        className={`px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition disabled:opacity-50 ${
                          on ? m.onCls : 'text-slate-500 hover:text-slate-700'}`}>
                        {m.label}
                      </button>
                    )
                  })}
                </div>
                <p className="text-[11.5px] text-slate-400 mt-1.5">
                  เลือกได้อย่างใดอย่างหนึ่ง หรือไม่เลือกเลย · OT พิเศษ = จ่ายแยก ระบบจะไม่คิด OT ตามปกติให้
                </p>
              </div>

              {/* what Xano computed on the last save */}
              <SectionHead icon={Calculator} title="ผลการคำนวณล่าสุด" note="ระบบคำนวณใหม่ทุกครั้งที่บันทึก" />
              <div className="grid grid-cols-3 gap-2">
                <Stat label="สาย (นาที)" value={late ? String(late) : '—'} tone={late ? 'text-rose-600' : 'text-slate-800'} />
                <Stat label="OT / ออกก่อน" value={ot ? fmtMoney(ot) : '—'} tone={ot < 0 ? 'text-rose-600' : ot > 0 ? 'text-emerald-600' : 'text-slate-800'} />
                <Stat label="ค่าเดินทาง" value={travelFee(row) ? fmtMoney(travelFee(row)) : '—'} />
              </div>
            </>
          )}

          {/* note (appended to clockin.remark[]) */}
          <SectionHead icon={StickyNote} title="หมายเหตุการแก้ไข" />
          <textarea rows={2} value={note} disabled={locked} onChange={(e) => setNote(e.target.value)}
            placeholder="เหตุผลที่แก้ไข เช่น ลืมสแกนตอนเข้างาน…" className={FIELD} />
          {notes.length > 0 && (
            <div className="space-y-1.5 mt-2.5">
              {notes.map((n, i) => (
                <div key={i} className="rounded-xl bg-slate-50 px-3 py-2">
                  <div className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{n?.message}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{fmtDateTime(n?.created_at)}</div>
                </div>
              ))}
            </div>
          )}

          {/* audit trail */}
          <SectionHead icon={History} title="ประวัติการลงเวลา" />
          {logs.length === 0 ? (
            <p className="text-[12.5px] text-slate-400">ยังไม่มีประวัติ</p>
          ) : (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
              {logs.map((l, i) => (
                <div key={i} className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11.5px] font-medium text-slate-500 uppercase">{l?.type || '—'}</span>
                    <span className="text-[11px] text-slate-400">{fmtDateTime(l?.created_at)}</span>
                  </div>
                  <div className="text-[12px] text-slate-600 break-words">{l?.message}</div>
                </div>
              ))}
            </div>
          )}

          {/* record provenance — who created/last touched the timetable ROW
              itself (the punch's own audit trail is the ประวัติ list above). */}
          <SectionHead icon={Fingerprint} title="ข้อมูลเรคอร์ด" note="employee_timetable" />
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-[12.5px]">
            <Meta label="ID" value={row?.id != null ? `#${row.id}` : '—'} />
            <Meta label="สร้างโดย" value={actorLabel(row?.creator, row?.created_by)} sub={fmtDateTime(row?.created_at)} />
            <Meta label="แก้ไขล่าสุดโดย" value={actorLabel(row?.modifier, row?.modified_by)} sub={fmtDateTime(row?.modified_at)} />
          </div>
        </div>

        {/* footer */}
        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {punched && !locked && !leave && (
              <button onClick={() => setConfirmClear(true)} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <Trash2 size={14} /> ล้างการลงเวลา
              </button>
            )}
            <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
            <button onClick={save} disabled={saving || locked}
              className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmClear && (
        <ConfirmDialog
          title="ล้างการลงเวลา"
          message="ลบเวลาเข้า-ออกของกะนี้ทั้งหมด (ตัวกะยังอยู่) — ระบบจะบันทึกไว้ในประวัติ"
          confirmLabel="ล้างการลงเวลา"
          tone="danger"
          onConfirm={clearPunch}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </div>
  )
}
